import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW } from "@/lib/userAccess";

export function ComisionesTable() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRepresentante, setSelectedRepresentante] = useState(null);
  const [pedidosDetalle, setPedidosDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  
  // Establecer fechas por defecto (último mes)
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const [fechaDesde, setFechaDesde] = useState(lastMonth.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(today.toISOString().split('T')[0]);
  
  const [comisiones, setComisiones] = useState([]);
  const [vendedores, setVendedores] = useState([]);

  useEffect(() => {
    cargarVendedores();
  }, []);

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      cargarComisiones();
    }
  }, [fechaDesde, fechaHasta]);

  const cargarVendedores = async () => {
    try {
      const { data, error } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS);

      if (error) throw error;
      setVendedores(data || []);
    } catch (error) {
      console.error('Error al cargar vendedores:', error);
      setError('Error al cargar vendedores');
    }
  };

  const cargarComisiones = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Primero obtenemos los pedidos con los datos del cliente y los items
      let query = supabase
        .from('orders')
        .select(`
          id,
          total,
          created_at,
          customer:customers(
            id,
            name,
            cod_rep
          ),
          items:order_items(
            quantity
          )
        `)
        .gte('created_at', fechaDesde)
        .lte('created_at', fechaHasta);

      // Si el usuario no tiene permiso para ver comisiones globales, filtrar por su ID
      if (!user.ver_comisiones_globales) {
        query = query.eq('customer.cod_rep', user.cod_rep);
      }

      const { data: pedidosData, error: pedidosError } = await query;

      if (pedidosError) throw pedidosError;

      // Obtenemos los códigos de representantes únicos
      const codReps = [...new Set(pedidosData
        ?.filter(pedido => pedido.customer?.cod_rep)
        .map(pedido => pedido.customer.cod_rep))];

      // Obtenemos los datos de los representantes
      const { data: representantesData, error: representantesError } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS)
        .in('cod_rep', codReps);

      if (representantesError) throw representantesError;

      // Creamos un mapa de representantes para acceso rápido
      const representantesMap = representantesData.reduce((acc, rep) => {
        acc[rep.cod_rep] = rep;
        return acc;
      }, {});

      // Calcular comisiones por vendedor
      const comisionesPorVendedor = {};
      
      pedidosData?.forEach(pedido => {
        if (!pedido.customer) return;
        
        const codRep = pedido.customer.cod_rep;
        const representante = codRep ? representantesMap[codRep] : null;
        
        // Calcular cantidad de prendas en este pedido
        const cantidadPrendas = pedido.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
        
        if (!codRep || !representante) {
          // Si no hay representante asignado
          if (!comisionesPorVendedor['no_specified']) {
            comisionesPorVendedor['no_specified'] = {
              nombre: 'Vendedor no especificado',
              rol: 'N/A',
              totalVentas: 0,
              comision: 0,
              codRep: 'no_specified',
              numeroPedidos: 0,
              cantidadPrendas: 0
            };
          }
          comisionesPorVendedor['no_specified'].totalVentas += pedido.total;
          comisionesPorVendedor['no_specified'].numeroPedidos += 1;
          comisionesPorVendedor['no_specified'].cantidadPrendas += cantidadPrendas;
          return;
        }

        if (!comisionesPorVendedor[codRep]) {
          comisionesPorVendedor[codRep] = {
            nombre: representante.name,
            rol: representante.role,
            totalVentas: 0,
            comision: 0,
            codRep: codRep,
            numeroPedidos: 0,
            cantidadPrendas: 0
          };
        }

        const comision = (pedido.total * representante.comision) / 100;
        comisionesPorVendedor[codRep].totalVentas += pedido.total;
        comisionesPorVendedor[codRep].comision += comision;
        comisionesPorVendedor[codRep].numeroPedidos += 1;
        comisionesPorVendedor[codRep].cantidadPrendas += cantidadPrendas;
      });

      // Filtrar vendedores sin ventas
      const comisionesFiltradas = Object.values(comisionesPorVendedor)
        .filter(c => c.totalVentas > 0)
        .sort((a, b) => b.totalVentas - a.totalVentas);

      setComisiones(comisionesFiltradas);
    } catch (error) {
      console.error('Error al cargar comisiones:', error);
      setError('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  const cargarDetallePedidos = async (codRep) => {
    try {
      console.log('Cargando detalles para representante:', codRep);
      setLoadingDetalle(true);
      setSelectedRepresentante(codRep);

      // Verificar permisos
      if (!user.ver_comisiones_globales && user.cod_rep !== codRep) {
        throw new Error('No tienes permiso para ver estos detalles');
      }

      // Primero obtenemos los pedidos usando el cod_rep directamente de la tabla orders
      let query = supabase
        .from('orders')
        .select(`
          id,
          number,
          total,
          status,
          date,
          cod_rep,
          customer:customers(
            name
          )
        `)
        .gte('date', fechaDesde)
        .lte('date', fechaHasta)
        .eq('cod_rep', codRep)
        .order('date', { ascending: false });

      // Si el usuario no tiene permiso para ver comisiones globales, asegurar que solo ve sus propios pedidos
      if (!user.ver_comisiones_globales) {
        query = query.eq('cod_rep', user.cod_rep);
      }

      const { data: pedidosData, error: pedidosError } = await query;

      if (pedidosError) throw pedidosError;

      // Obtenemos el porcentaje de comisión del representante
      const { data: representanteData, error: representanteError } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS)
        .eq('cod_rep', codRep)
        .single();

      if (representanteError) throw representanteError;

      const porcentajeComision = representanteData?.comision || 0;

      // Para cada pedido, obtenemos sus items
      const pedidosConItems = await Promise.all(
        pedidosData.map(async (pedido) => {
          const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select(`
              id,
              quantity,
              price,
              size,
              color,
              product:products(
                id,
                code,
                description
              )
            `)
            .eq('order_id', pedido.id);

          if (itemsError) throw itemsError;

          // Calculamos la comisión para este pedido
          const comision = (pedido.total * porcentajeComision) / 100;

          return {
            ...pedido,
            order_items: itemsData,
            comision
          };
        })
      );

      console.log('Pedidos cargados:', pedidosConItems);
      setPedidosDetalle(pedidosConItems);
    } catch (error) {
      console.error('Error al cargar detalle de pedidos:', error);
      setError(error.message || 'Error al cargar detalle de pedidos');
      setPedidosDetalle([]);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'partially_served_cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Pendiente',
      'processing': 'En proceso',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'partially_served_cancelled': 'Parcialmente servido cancelado'
    };
    return statusMap[status] || status;
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-4">
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="text-red-500 mb-4">{error}</div>
        )}

        {loading ? (
          <div>Cargando comisiones...</div>
        ) : comisiones.length === 0 ? (
          <div className="text-gray-500">No hay datos de comisiones para el período seleccionado</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="text-right">Nº Pedidos</TableHead>
                <TableHead className="text-right">Cantidad Prendas</TableHead>
                <TableHead className="text-right">Total Ventas</TableHead>
                <TableHead className="text-right">Comisión</TableHead>
                <TableHead className="text-center">Detalles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comisiones.map((comision, index) => (
                <TableRow key={index} className={comision.rol === 'N/A' ? 'bg-gray-50' : ''}>
                  <TableCell>{comision.nombre}</TableCell>
                  <TableCell>{comision.rol}</TableCell>
                  <TableCell className="text-right">
                    {comision.numeroPedidos}
                  </TableCell>
                  <TableCell className="text-right">
                    {comision.cantidadPrendas.toLocaleString('es-ES')}
                  </TableCell>
                  <TableCell className="text-right">
                    {new Intl.NumberFormat('es-ES', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format(comision.totalVentas)}
                  </TableCell>
                  <TableCell className="text-right">
                    {new Intl.NumberFormat('es-ES', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format(comision.comision)}
                  </TableCell>
                  <TableCell className="text-center">
                    {comision.rol !== 'N/A' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Navegar al dashboard del representante
                          window.location.hash = `#/dashboard-representante/${comision.codRep}`;
                        }}
                      >
                        Dashboard
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog open={!!selectedRepresentante} onOpenChange={() => setSelectedRepresentante(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>
                Detalle de Pedidos - {comisiones.find(c => c.codRep === selectedRepresentante)?.nombre}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDetalle ? (
              <div>Cargando detalles...</div>
            ) : pedidosDetalle.length === 0 ? (
              <div className="text-gray-500">No hay pedidos para mostrar en este período</div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-2">
                <div className="space-y-6">
                  {pedidosDetalle.map((pedido) => (
                    <div key={pedido.id} className="border rounded-lg p-4 space-y-4 bg-white">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold">Pedido #{pedido.number}</h3>
                          <p className="text-sm text-gray-500">
                            {new Date(pedido.date).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(pedido.status)}`}>
                            {getStatusText(pedido.status)}
                          </span>
                          <div className="flex flex-col items-end">
                            <span className="font-semibold">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(pedido.total)}
                            </span>
                            <span className="text-sm text-green-600">
                              Comisión: {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(pedido.comision)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-sm">
                        <p className="font-medium">Cliente: {pedido.customer?.name}</p>
                      </div>

                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="whitespace-nowrap">Código</TableHead>
                              <TableHead className="whitespace-nowrap">Producto</TableHead>
                              <TableHead className="whitespace-nowrap">Talla</TableHead>
                              <TableHead className="whitespace-nowrap">Color</TableHead>
                              <TableHead className="text-right whitespace-nowrap">Cantidad</TableHead>
                              <TableHead className="text-right whitespace-nowrap">Precio</TableHead>
                              <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pedido.order_items?.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="whitespace-nowrap">{item.product?.code}</TableCell>
                                <TableCell className="max-w-[200px] truncate">{item.product?.description}</TableCell>
                                <TableCell className="whitespace-nowrap">{item.size}</TableCell>
                                <TableCell className="whitespace-nowrap">{item.color}</TableCell>
                                <TableCell className="text-right whitespace-nowrap">{item.quantity}</TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                  {new Intl.NumberFormat('es-ES', {
                                    style: 'currency',
                                    currency: 'EUR'
                                  }).format(item.price)}
                                </TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                  {new Intl.NumberFormat('es-ES', {
                                    style: 'currency',
                                    currency: 'EUR'
                                  }).format(item.price * item.quantity)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
} 
